import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlineEnvelope, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeSlash, HiOutlineExclamationCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import AuthLayout from './AuthLayout';
import { useAuth } from '../../context/AuthContext';
import { validateEmail } from '../../utils/validation';
import api from '../../utils/api';

const SignIn = () => {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [remember, setRemember] = useState(false);

  const onChange = (e) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setErrors(p => ({ ...p, [e.target.name]: '' }));
    setServerErr('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    const emailErr = validateEmail(form.email);
    if (emailErr) errs.email = emailErr;
    if (!form.password) errs.password = 'Password is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const r = await api.post('/auth/signin', { email: form.email.trim(), password: form.password });
      if (r.data.success) {
        toast.success('Welcome back!');
        login(r.data.token, r.data.user, remember);
        nav('/');
      }
    } catch (err) {
      setServerErr(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth-heading">
        <h2>Welcome back</h2>
        <p>Sign in to manage your restaurant</p>
      </div>

      {serverErr && (
        <div className="alert alert-error">
          <HiOutlineExclamationCircle className="alert-icon" />
          <span>{serverErr}</span>
        </div>
      )}

      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label">Email address</label>
          <div className={`field-shell ${errors.email ? 'error' : ''}`}>
            <span className="field-shell-icon"><HiOutlineEnvelope /></span>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={onChange}
              autoFocus
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <span className="field-error">
              <HiOutlineExclamationCircle size={13} /> {errors.email}
            </span>
          )}
        </div>

        <div className="field">
          <label className="field-label">Password</label>
          <div className={`field-shell ${errors.password ? 'error' : ''}`}>
            <span className="field-shell-icon"><HiOutlineLockClosed /></span>
            <input
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={onChange}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="field-shell-action"
              onClick={() => setShowPw(s => !s)}
              tabIndex={-1}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
            </button>
          </div>
          {errors.password && (
            <span className="field-error">
              <HiOutlineExclamationCircle size={13} /> {errors.password}
            </span>
          )}
        </div>

        <div className="helper-row">
          <label className="checkbox-row">
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
            <span>Remember me</span>
          </label>
          <Link to="/forgot-password" className="link">Forgot password?</Link>
        </div>

        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? <span className="spinner" /> : 'Sign in'}
        </button>
      </form>

      {/* <div className="auth-foot">
        Don't have an account? Ask your manager to create one for you.
      </div> */}
    </AuthLayout>
  );
};

export default SignIn;
